import React, { useState, useEffect } from 'react';
import { TextField, Typography, Box, Paper, Alert } from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';
import SignatureCanvas from '../SignatureCanvas';

interface ActivitiesDailyLivingProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Evaluee Information
  evaluee_name: string;

  // Household Activities - Pre-Injury Hours
  shopping_pre_injury: string;
  wash_ironing_clothing_pre_injury: string;
  cooking_pre_injury: string;
  household_maintenance_pre_injury: string;
  yard_maintenance_pre_injury: string;
  driving_family_appointments_pre_injury: string;
  car_care_repair_pre_injury: string;
  family_child_care_pre_injury: string;
  counseling_guidance_pre_injury: string;
  banking_record_keeping_pre_injury: string;
  other_activities_pre_injury: string;
  other_activities_pre_injury_specify: string;
  other_activities2_pre_injury: string;
  other_activities2_pre_injury_specify: string;

  // Household Activities - Post-Injury Hours
  shopping_post_injury: string;
  wash_ironing_clothing_post_injury: string;
  cooking_post_injury: string;
  household_maintenance_post_injury: string;
  yard_maintenance_post_injury: string;
  driving_family_appointments_post_injury: string;
  car_care_repair_post_injury: string;
  family_child_care_post_injury: string;
  counseling_guidance_post_injury: string;
  banking_record_keeping_post_injury: string;
  other_activities_post_injury: string;
  other_activities_post_injury_specify: string;
  other_activities2_post_injury: string;
  other_activities2_post_injury_specify: string;

  // Signature Information
  evaluee_signature_date: string;
  evaluee_signature: string;
  evaluee_signature_name: string;

  submitter_signature_date: string;
  submitter_signature: string;
  submitter_name: string;
}

const ActivitiesDailyLiving: React.FC<ActivitiesDailyLivingProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'activities_daily_living', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'activities_daily_living', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Evaluee Information
    evaluee_name: '',

    // Household Activities - Pre-Injury Hours
    shopping_pre_injury: '',
    wash_ironing_clothing_pre_injury: '',
    cooking_pre_injury: '',
    household_maintenance_pre_injury: '',
    yard_maintenance_pre_injury: '',
    driving_family_appointments_pre_injury: '',
    car_care_repair_pre_injury: '',
    family_child_care_pre_injury: '',
    counseling_guidance_pre_injury: '',
    banking_record_keeping_pre_injury: '',
    other_activities_pre_injury: '',
    other_activities_pre_injury_specify: '',
    other_activities2_pre_injury: '',
    other_activities2_pre_injury_specify: '',

    // Household Activities - Post-Injury Hours
    shopping_post_injury: '',
    wash_ironing_clothing_post_injury: '',
    cooking_post_injury: '',
    household_maintenance_post_injury: '',
    yard_maintenance_post_injury: '',
    driving_family_appointments_post_injury: '',
    car_care_repair_post_injury: '',
    family_child_care_post_injury: '',
    counseling_guidance_post_injury: '',
    banking_record_keeping_post_injury: '',
    other_activities_post_injury: '',
    other_activities_post_injury_specify: '',
    other_activities2_post_injury: '',
    other_activities2_post_injury_specify: '',

    // Signature Information
    evaluee_signature_date: '',
    evaluee_signature: '',
    evaluee_signature_name: '',

    submitter_signature_date: '',
    submitter_signature: '',
    submitter_name: '',
  });

  useEffect(() => {
    if (data) {
      setFormData(prev => ({ ...prev, ...actualFormData }));
    }
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const activities = [
    { key: 'shopping', label: 'Shopping' },
    { key: 'wash_ironing_clothing', label: 'Wash/ Ironing / Clothing Care' },
    { key: 'cooking', label: 'Cooking' },
    { key: 'household_maintenance', label: 'Household Maintenance' },
    { key: 'yard_maintenance', label: 'Yard Maintenance' },
    {
      key: 'driving_family_appointments',
      label: 'Driving family to Appointments',
    },
    { key: 'car_care_repair', label: 'Car Care & Repair' },
    { key: 'family_child_care', label: 'Family Child Care/Babysitting' },
    { key: 'counseling_guidance', label: 'Counseling & Guidance' },
    { key: 'banking_record_keeping', label: 'Banking & Record Keeping' },
  ];

  return (
    <Box>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontWeight: 'bold', color: '#1976d2' }}
      >
        Activities of Daily Living
      </Typography>

      <Alert severity='info' sx={{ mb: 3 }}>
        <Typography variant='body2'>
          <strong>Important:</strong> Please indicate the number of hours each
          activity was performed by the evaluee, before his/her injury and after
          the injury, weekly. If explanations are needed, indicate them in the
          explanations section on this form.
        </Typography>
      </Alert>

      {/* Evaluee Name */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Name of the Evaluee
        </Typography>
        <TextField
          fullWidth
          label='Evaluee Name'
          value={formData.evaluee_name}
          onChange={e => handleChange('evaluee_name', e.target.value)}
          required={isFieldRequiredLocal('evaluee_name')}
          sx={{ mb: 2 }}
        />
      </Paper>

      {/* Hours Performed Per Week Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Hours Performed Per Week
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          {/* Header Row */}
          <Box sx={{ display: 'flex', mb: 2, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                Household Activities
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: '150px' }}>
              <Typography
                variant='subtitle1'
                sx={{ fontWeight: 'bold', textAlign: 'center' }}
              >
                Pre-Injury
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: '150px' }}>
              <Typography
                variant='subtitle1'
                sx={{ fontWeight: 'bold', textAlign: 'center' }}
              >
                Post-Injury
              </Typography>
            </Box>
          </Box>

          {activities.map((activity, _index) => (
            <Box
              key={activity.key}
              sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}
            >
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant='body2'>{activity.label}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  fullWidth
                  type='number'
                  label='Hours'
                  value={
                    formData[`${activity.key}_pre_injury` as keyof FormData]
                  }
                  onChange={e =>
                    handleChange(`${activity.key}_pre_injury`, e.target.value)
                  }
                  inputProps={{ min: 0, step: 0.5 }}
                  size='small'
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  fullWidth
                  type='number'
                  label='Hours'
                  value={
                    formData[`${activity.key}_post_injury` as keyof FormData]
                  }
                  onChange={e =>
                    handleChange(`${activity.key}_post_injury`, e.target.value)
                  }
                  inputProps={{ min: 0, step: 0.5 }}
                  size='small'
                />
              </Box>
            </Box>
          ))}

          {/* Other Activities */}
          {[1, 2].map(num => (
            <Box
              key={`other${num}`}
              sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}
            >
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label={`Other ${num === 1 ? '' : num} (Please Specify)`}
                  value={
                    formData[
                      `other_activities${num === 2 ? '2' : ''}_pre_injury_specify` as keyof FormData
                    ]
                  }
                  onChange={e =>
                    handleChange(
                      `other_activities${num === 2 ? '2' : ''}_pre_injury_specify`,
                      e.target.value
                    )
                  }
                  size='small'
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  fullWidth
                  type='number'
                  label='Hours'
                  value={
                    formData[
                      `other_activities${num === 2 ? '2' : ''}_pre_injury` as keyof FormData
                    ]
                  }
                  onChange={e =>
                    handleChange(
                      `other_activities${num === 2 ? '2' : ''}_pre_injury`,
                      e.target.value
                    )
                  }
                  inputProps={{ min: 0, step: 0.5 }}
                  size='small'
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  fullWidth
                  type='number'
                  label='Hours'
                  value={
                    formData[
                      `other_activities${num === 2 ? '2' : ''}_post_injury` as keyof FormData
                    ]
                  }
                  onChange={e =>
                    handleChange(
                      `other_activities${num === 2 ? '2' : ''}_post_injury`,
                      e.target.value
                    )
                  }
                  inputProps={{ min: 0, step: 0.5 }}
                  size='small'
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Signatures Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant='h6' gutterBottom>
          Signatures
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Evaluee Signature */}
          <Box sx={{ flex: 1, minWidth: '400px' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Signature of Evaluee
              </Typography>

              <TextField
                fullWidth
                label='Name'
                value={formData.evaluee_signature_name}
                onChange={e =>
                  handleChange('evaluee_signature_name', e.target.value)
                }
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type='date'
                label='Date'
                value={formData.evaluee_signature_date}
                onChange={e =>
                  handleChange('evaluee_signature_date', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <SignatureCanvas
                value={formData.evaluee_signature}
                onChange={signature =>
                  handleChange('evaluee_signature', signature)
                }
                label='Evaluee Signature'
                width={350}
                height={120}
              />
            </Box>
          </Box>

          {/* Submitter Signature */}
          <Box sx={{ flex: 1, minWidth: '400px' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Signature of the Person Submitting this Form
              </Typography>

              <TextField
                fullWidth
                label='Name of the Person Submitting this Form (print)'
                value={formData.submitter_name}
                onChange={e => handleChange('submitter_name', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type='date'
                label='Date'
                value={formData.submitter_signature_date}
                onChange={e =>
                  handleChange('submitter_signature_date', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <SignatureCanvas
                value={formData.submitter_signature}
                onChange={signature =>
                  handleChange('submitter_signature', signature)
                }
                label='Submitter Signature'
                width={350}
                height={120}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ActivitiesDailyLiving;
